import { DateTime } from "luxon";
import { type FC, type ReactNode, useCallback, useMemo } from "react";
import { type ResolvedActualDay, resolveActualDayShifts } from "@/components/StaffTable/utils";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { useDepartmentNurseTypes } from "@/hooks/useDepartmentNurseTypes";
import { useFutureLookingCostlyStaff } from "@/hooks/useFutureLookingCostlyStaff";
import { CALL_INS_PAGE_SIZE, useGetLimitedCallOffs } from "@/hooks/useGetLimitedCallOffs";
import { useLastNameFirst } from "@/hooks/useNameFormat";
import { useNurseCosts } from "@/hooks/useNurseCosts";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useScheduleCacheEnabled } from "@/hooks/useScheduleWeekRules";
import { calculateTimeRange, getShiftDisplayDate, getVisibleWeekOffset } from "@/utils/dateUtils";
import {
	AbsenceType,
	type CalledOffShift,
	type Day,
	type DayItem,
	NotificationType,
	type NurseInShift,
	type PunchSummary,
	type ShiftRequest,
	type StatsCalculationParams,
} from "@/utils/models";
import { datesOfWeek, dayIndexOfDate, ONLY_DAY_INDEX, rowsOnDate, weekIndexOfDate } from "@/utils/panelWeeks";
import { computePunchSummary } from "@/utils/punchSummary";
import { calculateDayStats } from "@/utils/statsCalculations";
import { salariedFromSchedule, salariedFromWageHistory } from "@/utils/wageUtils";
import { useChosenDepartment } from "../ChosenDepartment";
import { FEATURE_FLAGS } from "../FeatureFlagsProvider";
import { useRequestsContext } from "../RequestsContext";
import { useWeekIndex } from "../WeekIndexProvider";
import { timeOffByNurseIdOf } from "./buildPtoByDayISO";
import { usePtoByDayISO } from "./usePtoByDayISO";
import { useWeekDays } from "./useWeekDays";
import { WeekPunchSummaryContext } from "./WeekPunchSummaryContext";
import { type WeekOfDate, WeekViewContext } from "./WeekViewContext";

type BaseDayItem = Omit<DayItem, "pto">;

const RECENT_CALL_INS_LOOKBACK_DAYS = 30;

// Messages on `CalledOffShift` that are management-initiated, NOT a nurse calling in.
// Anything else (including custom INH-3258 reasons like "Sick", "Family Obligations") is a call-in.
const NON_CALL_IN_MESSAGES = new Set<string>([
	AbsenceType.NO_SHOW,
	AbsenceType.OVERSTAFFED,
	AbsenceType.LOW_CENSUS,
	AbsenceType.CANCEL,
	AbsenceType.SUSPENSION,
]);

const isCallInMessage = (message: string | undefined | null): boolean => !!message && !NON_CALL_IN_MESSAGES.has(message);

/**
 * The shown week's day items for the staffing pages (Week, Day, Month, People, mobile): mounted around those routes
 * only, so no other page computes them. The week comes from `useWeekDays`: the schedule cache with
 * `staffing_schedule_cache` on, the old list with it off.
 */
export const WeekViewProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { viewStartDay } = useWeekIndex();
	const {
		weeks,
		weekIndex,
		nurses,
		getNurseById,
		people,
		getPerson,
		timeOff,
		wageData,
		scheduledShiftsById,
		firstDate,
		lastDate,
		isLoading,
		isError,
		isScheduleLoading,
		isScheduleError,
		isWindowLoading,
		refetch,
	} = useWeekDays();
	const isCacheEnabled = useScheduleCacheEnabled();
	const { chosenDepartment } = useChosenDepartment();
	const { isHospital } = useDepartmentFlags();
	const departmentNurseTypes = useDepartmentNurseTypes(chosenDepartment.department_id);
	// The roles the day stats count: with the cache on also the roles the weeks' people hold, so the stats don't change
	// when the roster (whose roles `departmentNurseTypes` adds) arrives.
	const statsRoles = useMemo(
		() => (isCacheEnabled ? [...new Set([...departmentNurseTypes, ...people.map((person) => person.role)])] : departmentNurseTypes),
		[isCacheEnabled, departmentNurseTypes, people],
	);
	// Everyone's time off and preferences: with the cache on the schedule's, which holds every row touching the weeks;
	// off, the old list's.
	const timeOffByNurseId = useMemo(
		() => (isCacheEnabled ? timeOffByNurseIdOf(timeOff) : new Map(nurses.map((nurse) => [nurse.id, nurse.employeeSpecificPreferences]))),
		[isCacheEnabled, nurses, timeOff],
	);
	const timeOffHolders = useMemo(() => [...timeOffByNurseId.values()].map((preferences) => ({ employeeSpecificPreferences: preferences })), [timeOffByNurseId]);
	// The cache's shifts say; the old list's don't, so with it the roster's wage history decides.
	const isShiftSalaried = useMemo(() => (isCacheEnabled ? salariedFromSchedule : salariedFromWageHistory(getNurseById)), [isCacheEnabled, getNurseById]);
	const timezone = chosenDepartment.timezone;
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const lastNameFirst = useLastNameFirst();
	const { staffRequests } = useRequestsContext();
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);

	// Every request by the day it shows on, for any day, not only the shown week's.
	const requestsByDay = useMemo<Map<number, ShiftRequest[]>>(
		() =>
			staffRequests.reduce((map, request) => {
				const requestDate = getShiftDisplayDate(request.message.shift_start, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes);

				const patchedRequestDate = requestDate.startOf("day");

				const dateKey: number = patchedRequestDate.toSeconds();
				map.set(dateKey, [...(map.get(dateKey) || []), request]);

				if (request.request_type === NotificationType.Swap && request.request_target_shift) {
					const targetShift = scheduledShiftsById.get(request.request_target_shift);
					if (targetShift) {
						const targetDate = getShiftDisplayDate(targetShift.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes);
						const targetDateKey: number = targetDate.startOf("day").toSeconds();
						if (targetDateKey !== dateKey) {
							map.set(targetDateKey, [...(map.get(targetDateKey) || []), request]);
						}
					}
				}

				return map;
			}, new Map<number, ShiftRequest[]>()),
		[staffRequests, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes, scheduledShiftsById],
	);

	const weekOfDate = useCallback(
		(date: DateTime): WeekOfDate | undefined => {
			const week = weeks[weekIndexOfDate(weeks, date)];
			const dayIndex = dayIndexOfDate(weeks, date);
			return week && dayIndex !== -1 ? { week, dayIndex } : undefined;
		},
		[weeks],
	);

	const requestsOnDate = useCallback((date: DateTime): ShiftRequest[] => requestsByDay.get(date.startOf("day").toSeconds()) ?? [], [requestsByDay]);

	const { baseWeekViewData, baseVisibleWeekViewData } = useMemo<{
		baseWeekViewData: BaseDayItem[];
		baseVisibleWeekViewData: BaseDayItem[];
	}>(() => {
		const empty = { baseWeekViewData: [], baseVisibleWeekViewData: [] };
		// With the cache off the old list's weeks come before its nurses; with it on, the weeks come with their people.
		if ((!isCacheEnabled && !nurses.length) || !weeks[weekIndex]) return empty;

		// Build a BaseDayItem for a specific (data-week, day of that week). Returns null when the source week
		// is missing or has no day there — the caller filters nulls.
		const buildBaseDayItem = (sourceWeekIndex: number, sourceDayIndex: number): BaseDayItem | null => {
			const sourceWeek = weeks[sourceWeekIndex];
			if (!sourceWeek) return null;
			const date = datesOfWeek(sourceWeek)[sourceDayIndex];
			if (!date) return null;

			// Each row's own day for the date: pairing the rows by position would mix dates where a row skips a day.
			const dayRows = rowsOnDate(sourceWeek.weeklyShifts, date);
			const days: Day[] = dayRows.map((row) => row.days[ONLY_DAY_INDEX]);
			const shifts: NurseInShift[] = days.flatMap((day) => day.nurses);
			const requests = requestsOnDate(date);

			// Stats must operate on the source data-week, not the visible window — OT, wages,
			// and totals are aligned to department.week_start_day and don't follow viewStartDay.
			const params: StatsCalculationParams = {
				nurses: people,
				department: chosenDepartment,
				timezone,
				rolesToDisplay: statsRoles,
				weeklyShifts: dayRows,
				dayIndex: ONLY_DAY_INDEX,
				wageData,
				jobManagementEnabled,
			};

			return {
				date,
				shifts,
				shiftsActual: days.flatMap((day) => day.nursesActual || []),
				requests,
				dayStats: calculateDayStats(params, !isHospital, timezone),
				dayStatsActual: calculateDayStats(params, !isHospital, timezone, true),
				...calculateTimeRange(shifts, requests),
			};
		};

		const baseWeekViewData = Array.from({ length: 7 }, (_, i) => buildBaseDayItem(weekIndex, i)).filter((item): item is BaseDayItem => item !== null);

		// Flag off → offset 0 → branch below reuses baseWeekViewData by identity, skipping rotation work.
		const offset = isDynamicWeekStartEnabled ? getVisibleWeekOffset(viewStartDay) : 0;
		const baseVisibleWeekViewData =
			offset === 0
				? baseWeekViewData
				: Array.from({ length: 7 }, (_, visiblePosition) => {
						const totalIndex = visiblePosition + offset;
						return totalIndex < 7 ? buildBaseDayItem(weekIndex, totalIndex) : buildBaseDayItem(weekIndex + 1, totalIndex - 7);
					}).filter((item): item is BaseDayItem => item !== null);

		return { baseWeekViewData, baseVisibleWeekViewData };
	}, [
		weeks,
		weekIndex,
		isCacheEnabled,
		nurses,
		people,
		requestsOnDate,
		chosenDepartment,
		timezone,
		isHospital,
		statsRoles,
		viewStartDay,
		isDynamicWeekStartEnabled,
		wageData,
		jobManagementEnabled,
	]);

	// Spans data-week + spillover days so aux maps (OT, PTO, call-offs, punches) cover both.
	// Identity-equal to baseWeekViewData when offset=0 — default users skip the union work.
	const baseDayItemsForAuxData = useMemo<BaseDayItem[]>(() => {
		if (baseVisibleWeekViewData === baseWeekViewData) return baseWeekViewData;
		const seen = new Set<string>();
		const out: BaseDayItem[] = [];
		for (const source of [baseWeekViewData, baseVisibleWeekViewData]) {
			for (const di of source) {
				const iso = di.date.toISODate();
				if (iso && !seen.has(iso)) {
					seen.add(iso);
					out.push(di);
				}
			}
		}
		return out;
	}, [baseWeekViewData, baseVisibleWeekViewData]);

	const futureLookingWeeklyOvertimeEnabled = useFeatureFlag(FEATURE_FLAGS.FutureLookingWeeklyOvertime);
	const { costlyShiftsByDay, shiftHoursById, actualShiftIds } = useFutureLookingCostlyStaff(baseDayItemsForAuxData, futureLookingWeeklyOvertimeEnabled);

	const weekStart = baseDayItemsForAuxData[0]?.date.startOf("day");
	const weekEnd = baseDayItemsForAuxData[baseDayItemsForAuxData.length - 1]?.date.endOf("day");
	const today = useMemo(() => DateTime.now().startOf("day"), []);

	const fromDate = useMemo<DateTime | undefined>(() => {
		const lookbackStart = today.minus({ days: RECENT_CALL_INS_LOOKBACK_DAYS });
		return weekStart && weekStart < lookbackStart ? weekStart : lookbackStart;
	}, [today, weekStart]);
	const toDate = useMemo<DateTime | undefined>(() => (weekEnd && weekEnd > today ? weekEnd : today), [today, weekEnd]);
	const { combinedData: callOffsResponse } = useGetLimitedCallOffs({
		page: 1,
		pageSize: CALL_INS_PAGE_SIZE,
		fromDate,
		toDate,
	});

	const callOffsByDayISO = useMemo<Map<string, CalledOffShift[]>>(() => {
		const map = new Map<string, CalledOffShift[]>();
		const results = callOffsResponse?.results ?? [];
		for (const callOff of results) {
			if (!isCallInMessage(callOff.message)) continue;
			const dayKey = getShiftDisplayDate(callOff.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes)
				.startOf("day")
				.toISODate();
			if (!dayKey) continue;
			const existing = map.get(dayKey);
			if (existing) existing.push(callOff);
			else map.set(dayKey, [callOff]);
		}
		return map;
	}, [callOffsResponse, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes]);

	const recentCallInsCounts = useMemo<Record<number, number>>(() => {
		const cutoff = today.minus({ days: RECENT_CALL_INS_LOOKBACK_DAYS });
		const counts: Record<number, number> = {};
		for (const callOff of callOffsResponse?.results ?? []) {
			if (!isCallInMessage(callOff.message)) continue;
			if (callOff.startTime < cutoff || callOff.startTime > today) continue;
			counts[callOff.nurseId] = (counts[callOff.nurseId] ?? 0) + 1;
		}
		return counts;
	}, [callOffsResponse, today]);

	const ptoByDayISO = usePtoByDayISO(
		baseDayItemsForAuxData.map((day) => day.date),
		timeOffHolders,
		chosenDepartment.dayStart,
		timezone,
	);

	const ptoCostsRangeStart = useMemo(() => weekStart?.toISODate() ?? undefined, [weekStart]);
	const ptoCostsRangeEnd = useMemo(() => weekEnd?.toISODate() ?? undefined, [weekEnd]);
	const { data: ptoCosts } = useNurseCosts({ startDate: ptoCostsRangeStart, endDate: ptoCostsRangeEnd });

	const ptoCostByDayByNurse = useMemo<Map<string, Map<number, number>>>(() => {
		const map = new Map<string, Map<number, number>>();
		if (!ptoCosts) return map;
		for (const item of ptoCosts) {
			const iso = item.date.toISODate();
			if (!iso) continue;
			let inner = map.get(iso);
			if (!inner) {
				inner = new Map<number, number>();
				map.set(iso, inner);
			}
			inner.set(item.nurseId, (inner.get(item.nurseId) ?? 0) + item.cost);
		}
		return map;
	}, [ptoCosts]);

	const getPtoCost = useCallback(
		(date: DateTime, nurseId: number): number | undefined => {
			const iso = date.toISODate();
			if (!iso) return undefined;
			return ptoCostByDayByNurse.get(iso)?.get(nurseId);
		},
		[ptoCostByDayByNurse],
	);

	// Whether a punch may be added to the schedule checks every scheduled shift, an overnight one from the day either side
	// of the week included, so the summaries wait for the weeks either side.
	const punchSummaryReady = callOffsResponse !== undefined && !isWindowLoading;

	// Keep `weekViewData`'s deps synchronous-only so its identity stays stable
	// when async queries resolve post-paint — protects the staffing grid's memo barriers.
	const weekViewData = useMemo<DayItem[]>(() => {
		return baseWeekViewData.map((day) => {
			const iso = day.date.toISODate() ?? "";
			const pto = ptoByDayISO.get(iso) ?? [];
			return { ...day, pto };
		});
	}, [baseWeekViewData, ptoByDayISO]);

	const visibleWeekViewData = useMemo<DayItem[]>(() => {
		// Reuse weekViewData when offset is 0 so identity stays stable for downstream memos.
		if (baseVisibleWeekViewData === baseWeekViewData) return weekViewData;
		return baseVisibleWeekViewData.map((day) => {
			const iso = day.date.toISODate() ?? "";
			const pto = ptoByDayISO.get(iso) ?? [];
			return { ...day, pto };
		});
	}, [baseVisibleWeekViewData, baseWeekViewData, weekViewData, ptoByDayISO]);

	// Outside the `punchSummaryReady` gate below because resolving shifts needs no other async data. The
	// grouped sections are all punch-summary derived and so stay empty until that gate opens, but the
	// timeline, the row counts and `nurseIdsToIgnore` read this map directly and can populate sooner. Whether a
	// shift expects a punch is read from the weeks' own people and shifts, so neither waits for the roster.
	const resolvedActualByDate = useMemo<Map<string, ResolvedActualDay>>(() => {
		const map = new Map<string, ResolvedActualDay>();
		for (const day of baseDayItemsForAuxData) {
			const iso = day.date.toISODate();
			if (!iso) continue;
			map.set(
				iso,
				resolveActualDayShifts({
					scheduledShifts: day.shifts,
					actualShifts: day.shiftsActual,
					getPerson,
					isSalaried: isShiftSalaried,
					ptoNurseIds: new Set((ptoByDayISO.get(iso) ?? []).map((preference) => preference.nurse_id)),
				}),
			);
		}
		return map;
	}, [baseDayItemsForAuxData, getPerson, isShiftSalaried, ptoByDayISO]);

	const scheduledShiftsByNurseIdForEligibility = useMemo<Map<number, NurseInShift[]>>(() => {
		const byNurseId = new Map<number, NurseInShift[]>();
		for (const shift of scheduledShiftsById.values()) {
			const existing = byNurseId.get(shift.nurseId);
			if (existing) existing.push(shift);
			else byNurseId.set(shift.nurseId, [shift]);
		}
		return byNurseId;
	}, [scheduledShiftsById]);

	const punchSummariesByDate = useMemo<Map<string, PunchSummary>>(() => {
		const map = new Map<string, PunchSummary>();
		if (!punchSummaryReady) return map;
		for (const day of baseDayItemsForAuxData) {
			const iso = day.date.toISODate();
			const resolvedActual = iso && resolvedActualByDate.get(iso);
			if (!iso || !resolvedActual) continue;
			map.set(
				iso,
				computePunchSummary({
					shifts: day.shifts,
					scheduledShiftsByNurseIdForEligibility,
					resolvedActual,
					getPerson,
					isSalaried: isShiftSalaried,
					department: chosenDepartment,
					pto: ptoByDayISO.get(iso) ?? [],
					jobManagementEnabled,
					lastNameFirst,
					costlyShiftsForDay: costlyShiftsByDay.get(iso) ?? [],
					shiftHoursById,
					callOffsForDay: callOffsByDayISO.get(iso) ?? [],
				}),
			);
		}
		return map;
	}, [
		baseDayItemsForAuxData,
		scheduledShiftsByNurseIdForEligibility,
		getPerson,
		isShiftSalaried,
		chosenDepartment,
		ptoByDayISO,
		jobManagementEnabled,
		lastNameFirst,
		costlyShiftsByDay,
		shiftHoursById,
		callOffsByDayISO,
		punchSummaryReady,
		resolvedActualByDate,
	]);

	const contextValue = useMemo(
		() => ({
			weekViewData,
			visibleWeekViewData,
			costlyShiftsByDay,
			shiftHoursById,
			actualShiftIds,
			recentCallInsCounts,
			getPtoCost,
			requestsOnDate,
			scheduleWeeks: weeks,
			scheduleWeekIndex: weekIndex,
			scheduleNurses: nurses,
			getScheduleNurse: getNurseById,
			schedulePeople: people,
			getSchedulePerson: getPerson,
			timeOffByNurseId,
			isShiftSalaried,
			weekOfDate,
			firstDate,
			lastDate,
			isLoading,
			isError,
			isScheduleLoading,
			isScheduleError,
			refetch,
		}),
		[
			weekViewData,
			visibleWeekViewData,
			costlyShiftsByDay,
			shiftHoursById,
			actualShiftIds,
			recentCallInsCounts,
			getPtoCost,
			requestsOnDate,
			weeks,
			weekIndex,
			nurses,
			getNurseById,
			people,
			getPerson,
			timeOffByNurseId,
			isShiftSalaried,
			weekOfDate,
			firstDate,
			lastDate,
			isLoading,
			isError,
			isScheduleLoading,
			isScheduleError,
			refetch,
		],
	);

	const punchSummaryContextValue = useMemo(() => ({ punchSummariesByDate, resolvedActualByDate }), [punchSummariesByDate, resolvedActualByDate]);

	return (
		<WeekViewContext.Provider value={contextValue}>
			<WeekPunchSummaryContext.Provider value={punchSummaryContextValue}>{children}</WeekPunchSummaryContext.Provider>
		</WeekViewContext.Provider>
	);
};
